import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import type { IUser } from '@/common/interfaces/user.interface';
import { V3_APP_BASE_URL } from '@/constants/Config';
import { getAccountType } from '@/utilities/CommonUtils';

interface IErrRes {
  status: string | number;
  data: { message: string };
}

interface IUpdateUser {
  id: string;
  account_id: number;
  isNewAccount: boolean;
  invalidPassword?: boolean;
  invalidEmail?: boolean;
}

interface IUpdateUserdetails {
  status: string | number;
  message: string;
  invalidPassword?: boolean;
  emailExists?: boolean;
  isUserDetailsUpdated?: boolean;
}

/** Defining a service using a base URL and expected endpoints  */
export const userQuery = createApi({
  reducerPath: 'userQuery',
  tagTypes: ['User'],
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getUserHashLogin: builder.query({
      query: (param) => ({ url: `api/v4/login?login=${param}&noRedirect=true`, method: 'get' }),
    }),
    getUser: builder.query<IUser, string>({
      query: (name) => ({ url: `api/cp/${name}`, method: 'get' }),
      providesTags: ['User'],
      transformResponse: (response: { user: IUser }) => response.user,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    updateUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `api/v4/user/${id}`,
        method: 'PUT',
        data: { ...data, clientType: getAccountType(data).toLowerCase() },
      }),
      invalidatesTags: ['User'],
      transformResponse: (response: { user: IUpdateUser }) => response.user,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    updateUserDetails: builder.mutation({
      query: ({ id, data }) => ({
        url: `api/cp/user/${id}`,
        method: 'PUT',
        data: { ...data },
      }),
      transformResponse: (response: IUpdateUserdetails) => response,
    }),
    logout: builder.mutation({
      query: () => ({
        url: `api/v4/logout`,
        method: 'POST',
      }),
    }),
    updateUserReqModalSeen: builder.mutation({
      query: () => ({
        url: 'api/cp/user/member-invite-modal-status',
        method: 'post',
      }),
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const {
  useGetUserQuery,
  useGetUserHashLoginQuery,
  useUpdateUserMutation,
  useUpdateUserDetailsMutation,
  useLogoutMutation,
  useUpdateUserReqModalSeenMutation,
} = userQuery;
